import { useSearchParams } from 'react-router-dom'

import type { OutputOptionKey } from './rundown-output.const'
import { OUTPUT_OPTIONS_CONFIG } from './rundown-output.const'

export function useRundownOutputOptions() {
  const [searchParams] = useSearchParams()

  /**
   * Function that looks up an Output option in the search params
   * and returns the value or a default value from the config.
   *
   * These values are hand-typed into a URL, so junk must never change
   * behaviour. **Mirror the option's default when comparing:**
   *
   * default `on`  -> only an explicit `off` turns it off:  `!== OFF`
   * default `off` -> only an explicit `on` turns it on:    `=== ON`
   *
   * Either way `?titles=1` or a typo like `?titles=of` lands on the documented
   * default rather than silently flipping the option. Comparing the other way
   * round breaks that: `=== ON` on a default-on option hides content for any
   * value that isn't exactly `on`.
   *
   * @example
   *   // The key can be a const or an autocompleted string
   *   getOption(HEADER_POSITION) // Works
   *   getOption('header-position') // Works
   *
   * @throws If an invalid key is passed it will warn at compile time and
   *   throw an error at runtime.
   */
  function getOption<K extends OutputOptionKey>(key: K): string {
    const validOption = OUTPUT_OPTIONS_CONFIG[key]
    if (!validOption) throw new Error('Invalid Output option')

    // Lookup option in params
    const value = searchParams.get(key)
    if (value) return value

    // Fallback to default value
    const defaultValue = OUTPUT_OPTIONS_CONFIG[key]?.default
    if (defaultValue) return defaultValue

    // Fallback to empty string
    return ''
  }

  return {
    getOption,
  }
}
